import { CircleAlert, CircleCheck, CircleHelp, CircleX, MinusCircle } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import {
  type DiffStatus,
  computeDiffStatus,
} from '~/components/common/enrichment-diff-table/diff-status.util';
import { EnrichmentOptContent } from '~/components/common/enrichment-opt-content';
import { ProductDetailsComparison } from '~/components/common/product-details-comparison/product-details-comparison';
import type { ProductOptimization } from '~/types/enrichment';
import { cn } from '~/utils/cn';

import type { DeliveredFieldStatus } from '../../utils/delivery-field-keys';
import { parseEnrichedProductDetailAttrs } from '../../utils/parse-enriched-product-details';

/** Shared column template so the one-time header and every row line up.
 *  Single column on mobile (each value stacks under its own inline label).
 *  `items-start` is explicit, not left to Grid's default `stretch`, so every
 *  column's text starts at the same top edge regardless of another column's
 *  height. */
export const ATTRIBUTE_ROW_GRID = 'grid grid-cols-1 items-start gap-x-6 gap-y-2 md:grid-cols-3';

/** Solid dot color per diff status — a content-sized marker next to the
 *  enriched value, not a full-cell fill. A one-word value ("adult") inside a
 *  background box that spans the whole column reads as an empty rectangle;
 *  a dot carries the same "this changed" signal at the size the content
 *  actually needs. */
const DIFF_DOT_CLASS: Record<DiffStatus, string> = {
  enriched: 'bg-success',
  modified: 'bg-warning',
  removed: 'bg-error',
  unchanged: '',
};

/** Delivered-status icon + color — an inline glyph next to the value rather
 *  than a pill badge, so the Delivered column stays a single line like its
 *  two neighbors instead of a badge-height row plus a separate value line. */
const STATUS_ICON: Record<DeliveredFieldStatus, LucideIcon> = {
  sent: CircleCheck,
  truncated: CircleAlert,
  failed: CircleX,
  not_sent: MinusCircle,
  unknown: CircleHelp,
};

const STATUS_ICON_CLASS: Record<DeliveredFieldStatus, string> = {
  sent: 'text-success',
  truncated: 'text-warning',
  failed: 'text-error',
  not_sent: 'text-muted-foreground',
  unknown: 'text-muted-foreground',
};

interface Props {
  opt: ProductOptimization;
  deliveryStatus: DeliveredFieldStatus;
  deliveryError?: string;
  /** Best-effort value read from the delivered payload; only present when the
   *  selected product's payload was fetched and the field could be resolved
   *  from it (see `extractDeliveredValue` — it never guesses). */
  deliveredValue?: string;
}

/**
 * One row of the compare table: the attribute name, then its pre-enrichment,
 * enriched and delivered values in three aligned columns under the table's
 * single header (`DeliveryCompareTableHeader`, rendered once by the page) —
 * no per-row column labels, so N attributes don't repeat the same three
 * headings N times. A changed enriched value gets a small content-sized dot
 * (color per diff status), not a full-cell background fill.
 *
 * `product-details` is the one exception: its comparison doesn't reduce to a
 * line of text, so it renders through the existing dedicated
 * `ProductDetailsComparison` two-pane widget, with the delivered status
 * appended below rather than squeezed into a third grid column.
 */
export function DeliveryCompareAttributeRow({
  opt,
  deliveryStatus,
  deliveryError,
  deliveredValue,
}: Props) {
  const { t } = useTranslation('catalog-operations');
  const rowTestId = `delivery-compare-attribute-row-${opt.field}`;

  if (opt.contentType === 'product-details') {
    const enrichedAttrs = parseEnrichedProductDetailAttrs(opt.value);
    return (
      <div className="space-y-3 border-b py-4 last:border-b-0" data-testid={rowTestId}>
        <RowLabel label={opt.label} />
        <ProductDetailsComparison
          currentAttrs={opt.currentAttributes ?? []}
          enrichedAttrs={enrichedAttrs}
          layout="panels"
          label={opt.label}
          testId={`${rowTestId}-comparison`}
        />
        <DeliveredPane
          testId={`${rowTestId}-delivered`}
          status={deliveryStatus}
          {...(deliveryError ? { error: deliveryError } : {})}
          note={t('deliveryCompare.attributeTable.structuredNoValue')}
        />
      </div>
    );
  }

  const diffStatus = computeDiffStatus(opt.currentValue, opt.value);
  return (
    <div className="border-b py-4 last:border-b-0" data-testid={rowTestId}>
      <RowLabel label={opt.label} />
      <div className={ATTRIBUTE_ROW_GRID}>
        <div className="min-w-0 break-words" data-testid={`${rowTestId}-pre-enrichment`}>
          <MobileLabel text={t('deliveryCompare.attributeTable.preEnrichment')} />
          {opt.currentValue ? (
            <EnrichmentOptContent
              value={opt.currentValue}
              type={opt.contentType}
              testId={`${rowTestId}-pre-enrichment-content`}
            />
          ) : (
            <EmptyValue />
          )}
        </div>
        <div className="min-w-0" data-testid={`${rowTestId}-enriched`}>
          <MobileLabel text={t('deliveryCompare.attributeTable.enriched')} />
          {opt.value ? (
            <div className="flex items-start gap-2">
              {diffStatus !== 'unchanged' && (
                <span
                  className={cn(
                    'mt-1.5 size-1.5 shrink-0 rounded-full',
                    DIFF_DOT_CLASS[diffStatus],
                  )}
                  title={t(`deliveryCompare.attributeTable.changedHint.${diffStatus}`)}
                />
              )}
              {/* `min-w-0` — a flex item defaults to `min-width: auto`, so an
                  unbroken token (a long URL, say) would otherwise force this
                  row wider instead of wrapping. `break-words` wraps it. */}
              <div className="min-w-0 flex-1 break-words">
                <EnrichmentOptContent
                  value={opt.value}
                  type={opt.contentType}
                  testId={`${rowTestId}-enriched-content`}
                />
              </div>
            </div>
          ) : (
            <EmptyValue />
          )}
        </div>
        <div className="min-w-0 break-words">
          <MobileLabel text={t('deliveryCompare.attributeTable.delivered')} />
          <DeliveredPane
            testId={`${rowTestId}-delivered`}
            status={deliveryStatus}
            {...(deliveryError ? { error: deliveryError } : {})}
            {...(deliveredValue ? { value: deliveredValue } : {})}
            enrichedValue={opt.value}
            contentType={opt.contentType}
          />
        </div>
      </div>
    </div>
  );
}

function RowLabel({ label }: { label: string }) {
  return <p className="text-foreground mb-2 text-sm font-semibold">{label}</p>;
}

/** Repeats the column name inline, but only below `md` where the three
 *  columns stack and the shared header above is no longer beside the value —
 *  without it a narrow screen would show three unlabeled stacked lines. */
function MobileLabel({ text }: { text: string }) {
  return <p className="text-muted-foreground mb-0.5 text-xs md:hidden">{text}</p>;
}

function EmptyValue() {
  return <p className="text-muted-foreground text-sm">—</p>;
}

function DeliveredPane({
  testId,
  status,
  error,
  value,
  enrichedValue,
  contentType,
  note,
}: {
  /** Automation hook for the delivered cell; the status icon gets `${testId}-status-{status}`. */
  testId: string;
  status: DeliveredFieldStatus;
  error?: string;
  /** Best-effort value read from the delivered payload. */
  value?: string;
  /** Fallback shown when the payload wasn't fetched/parseable but something
   *  was still attempted (`sent`/`truncated`/`failed`) — the enriched value
   *  is what would have been sent, so it's an honest stand-in, never shown
   *  for `not_sent`/`unknown` where nothing was actually delivered. */
  enrichedValue?: string;
  contentType?: ProductOptimization['contentType'];
  note?: string;
}) {
  const { t } = useTranslation('catalog-operations');
  const Icon = STATUS_ICON[status];
  const attempted = status === 'sent' || status === 'truncated' || status === 'failed';
  const displayValue = attempted ? (value ?? enrichedValue) : undefined;

  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-start gap-1.5">
        <Icon
          className={cn('mt-0.5 size-3.5 shrink-0', STATUS_ICON_CLASS[status])}
          aria-hidden="true"
          data-testid={`${testId}-status-${status.replace(/_/g, '-')}`}
        />
        {displayValue && contentType ? (
          <div className="min-w-0 flex-1">
            <EnrichmentOptContent
              value={displayValue}
              type={contentType}
              testId={`${testId}-content`}
            />
          </div>
        ) : (
          <span className={cn('text-sm', STATUS_ICON_CLASS[status])}>
            {t(`deliveryCompare.fieldStatus.${status}`)}
          </span>
        )}
      </div>
      {status === 'failed' && error ? (
        <p className="text-error text-xs" data-testid={`${testId}-error`}>
          {error}
        </p>
      ) : null}
      {!displayValue && note && (status === 'sent' || status === 'truncated') ? (
        <p className="text-muted-foreground text-xs italic">{note}</p>
      ) : null}
    </div>
  );
}
